import { createContext, useCallback, useContext } from 'react';

import useInsightsGroupTypeManagement from 'components/insights/hooks/useInsightsGroupTypeManagement';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import ReferralsPage from './ReferralsPage';

type ReferralsInsightsContextType = {
  profile: Profile;
  sendingGroupType: GroupType | undefined;
  receivingGroupTypes: GroupType[];
  onTabChange: (groupType: GroupType) => void;
};

export const ReferralsInsightsContext = createContext<ReferralsInsightsContextType>({
  profile: {} as Profile,
  sendingGroupType: undefined,
  receivingGroupTypes: [],
  onTabChange: () => {},
});

export const useReferralsInsightsContext = () => useContext(ReferralsInsightsContext);

// Managed-only group types first, then provided; within each group, sort by creation date
const buildGroupTypes = (managed: GroupType[], providedIds: Set<string>) =>
  [...managed].sort((a, b) => {
    const providedDiff = Number(providedIds.has(a.id)) - Number(providedIds.has(b.id));
    return providedDiff || (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
  });

const ReferralsContainer = () => {
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { setSelectedReceivingGroupType, setSendingGroupTypeId, resetFilters } = useReferralsInsightsActions();

  const {
    profile,
    predecessorGroupType: sendingGroupType,
    groupTypes: receivingGroupTypes,
    handleTabChange,
  } = useInsightsGroupTypeManagement({
    preferenceKey: 'referralsProviderType',
    buildGroupTypes: useCallback(buildGroupTypes, []),
    setSelectedGroupType: setSelectedReceivingGroupType,
    setPredecessorGroupTypeId: setSendingGroupTypeId,
    resetFilters,
    selectedGroupType: selectedReceivingGroupType,
  });

  return (
    <ReferralsInsightsContext value={{ profile, sendingGroupType, receivingGroupTypes, onTabChange: handleTabChange }}>
      {sendingGroupType && selectedReceivingGroupType && <ReferralsPage />}
    </ReferralsInsightsContext>
  );
};

export default ReferralsContainer;
